import {
  discoverMeaningsResultSchema,
  generateMeaningPackagesResultSchema,
  type DiscoverMeaningsRequest,
  type DiscoverMeaningsResult,
  type GenerateMeaningPackagesRequest,
  type GenerateMeaningPackagesResult,
} from "../../../contracts/ai/schemas.ts";
import type { AiCallOptions, VocabularyAi } from "../../../application/ports/ai.ts";
import { ProductError } from "../../../contracts/errors.ts";

export class HttpVocabularyAi implements VocabularyAi {
  constructor(
    private readonly baseUrl: string,
    private readonly fetchFn: typeof fetch = fetch,
  ) {}

  async discoverMeanings(
    request: DiscoverMeaningsRequest,
    options: AiCallOptions,
  ): Promise<DiscoverMeaningsResult> {
    return this.post("/api/v1/meanings/discover", request, options, discoverMeaningsResultSchema);
  }

  async generateMeaningPackages(
    request: GenerateMeaningPackagesRequest,
    options: AiCallOptions,
  ): Promise<GenerateMeaningPackagesResult> {
    return this.post("/api/v1/meanings/generate", request, options, generateMeaningPackagesResultSchema);
  }

  private async post<T>(
    path: string,
    body: unknown,
    options: AiCallOptions,
    schema: { parse: (value: unknown) => T },
  ): Promise<T> {
    let response: Response;
    try {
      response = await this.fetchFn(`${this.baseUrl.replace(/\/$/, "")}${path}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
        signal: options.signal,
      });
    } catch {
      if (options.signal?.aborted) {
        throw new ProductError("cancelled", "Generation was cancelled.");
      }
      throw new ProductError("offline", "The network request failed. Check your connection.", true);
    }
    if (!response.ok) {
      const code =
        response.status === 401 || response.status === 403
          ? "invalid_key"
          : response.status === 429
            ? "rate_limited"
            : response.status === 404
              ? "model_unavailable"
              : response.status === 408 || response.status === 504
                ? "timeout"
                : "generation_failed";
      throw new ProductError(
        code,
        `The server returned ${response.status}.`,
        response.status >= 500 || code === "rate_limited" || code === "timeout",
      );
    }
    const json: unknown = await response.json();
    try {
      return schema.parse(json);
    } catch {
      throw new ProductError("invalid_response", "The server returned an invalid vocabulary payload.");
    }
  }
}
